import { supabase } from "@/api/supabase";
import type { Tables } from "@/types/database";

/** Ports `driver_locations_data_source.dart` / `DriverLocationEntity`. */

export type Location = {
  id: number;
  name: string | null;
  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  phone: string | null;
  lat: number;
  lng: number;
  /** Already the discounted/network price as stored — no math applied here. `null` if not a positive number. */
  price: number | null;
  /** WEX retail price (`locations.retail_price`, synced daily). `null` if not a positive number. */
  retailPrice: number | null;
  /** Looked up against `location_chain_config` for the marker's brand logo — see `features/map/lib/station-logo.ts`. */
  chainId: number | null;
};

type LocationRow = Pick<
  Tables<"locations">,
  | "id"
  | "name"
  | "address1"
  | "address2"
  | "city"
  | "state"
  | "zip"
  | "phone"
  | "latitude"
  | "longitude"
  | "price"
  | "retail_price"
  | "chain_id"
>;

function mapLocation(row: LocationRow): Location {
  return {
    id: row.id,
    name: row.name,
    address1: row.address1,
    address2: row.address2,
    city: row.city,
    state: row.state,
    zip: row.zip,
    phone: row.phone,
    // Guaranteed non-null by the query's own `.not(..., "is", null)` filters.
    lat: row.latitude as number,
    lng: row.longitude as number,
    price: row.price !== null && row.price > 0 ? row.price : null,
    retailPrice: row.retail_price !== null && row.retail_price > 0 ? row.retail_price : null,
    chainId: row.chain_id,
  };
}

const PAGE_SIZE = 1000;
const LOCATION_COLUMNS = "id, name, address1, address2, city, state, zip, phone, latitude, longitude, price, retail_price, chain_id";

export type LatLngBounds = { minLat: number; maxLat: number; minLng: number; maxLng: number };

/** Shared pagination loop — 1000 rows at a time (PostgREST's per-request cap), looped until a page comes back short. */
async function fetchAllLocations(bounds?: LatLngBounds): Promise<Location[]> {
  const results: Location[] = [];
  let from = 0;
  for (;;) {
    let query = supabase
      .from("locations")
      .select(LOCATION_COLUMNS)
      .gt("price", 0)
      .not("latitude", "is", null)
      .not("longitude", "is", null);
    if (bounds) {
      query = query
        .gte("latitude", bounds.minLat)
        .lte("latitude", bounds.maxLat)
        .gte("longitude", bounds.minLng)
        .lte("longitude", bounds.maxLng);
    }
    const { data, error } = await query.range(from, from + PAGE_SIZE - 1);
    if (error) {
      throw error;
    }
    results.push(...data.map(mapLocation));
    if (data.length < PAGE_SIZE) {
      break;
    }
    from += PAGE_SIZE;
  }
  return results;
}

/**
 * Every location with a positive price and known coordinates — the
 * driver map's full, pannable-anywhere view needs the whole set. The
 * Flutter app fetches this same unfiltered-by-company set for every
 * driver.
 */
export function getLocations(): Promise<Location[]> {
  return fetchAllLocations();
}

/**
 * Locations within a lat/lng box — for the trip-route planner, which only
 * ever needs stations near one specific route, not the entire nationwide
 * table. Fetching everything there (thousands of rows, many paginated
 * round trips) was the dominant cost behind a slow "Finding route" —
 * scoping the query up front avoids it.
 */
export function getLocationsInBounds(bounds: LatLngBounds): Promise<Location[]> {
  return fetchAllLocations(bounds);
}

/**
 * The company a driver's assigned card belongs to — used to scope which
 * discount settings apply to the discounted-price breakdown shown on the
 * map. `null` if the driver's card has no assignment (a driver browsing
 * the map with no company yet just sees the raw price, no discount rows).
 */
export async function getCompanyIdForDriver(driverEmail: string): Promise<number | null> {
  const { data, error } = await supabase
    .from("company_cards")
    .select("company_id")
    .eq("driver_email", driverEmail)
    .limit(1);
  if (error) {
    throw error;
  }
  return data[0]?.company_id ?? null;
}
